import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { InteriorSlide, SlideBanner } from './SlideShell';
import type { SlideProps } from './SlideShell';
import { PdfImage } from '../PdfImage';
import { boehringerImages } from '../../data/boehringerSnak';

/** The live Boehringer T2D/CVD Snak shown as one short story, screen by screen. */
const SNAK_WALKTHROUGH = [{
  crop: boehringerImages.statScreen,
  alt: 'Opening stat: 332,507 Danes diagnosed with type 2 diabetes',
  caption: '332,507 people in Denmark diagnosed with type 2 diabetes'
}, {
  crop: boehringerImages.quizScreen,
  alt: 'Quiz asking what share of T2D and CVD patients receive neither SGLT2 nor GLP-1',
  caption: 'What share of patients with T2D and CVD receive neither SGLT2 nor GLP-1?'
}, {
  crop: boehringerImages.correctScreen,
  alt: 'Feedback screen: Correct! 47% lack organ-protective treatment',
  caption: 'Correct! 47% are not on organ-protective treatment'
}, {
  crop: boehringerImages.doubtScreen,
  alt: 'Reflection screen asking whether all patients are on organ-protective treatment',
  caption: 'Are all your T2D + CVD patients on organ-protective treatment? In doubt?'
}, {
  crop: boehringerImages.ctaScreen,
  alt: 'Call-to-action menu: Want to know more?',
  caption: 'Want to know more? Choose an option'
}] as const;
const PRINCIPLES = ['Short enough to finish.', 'Interesting enough to continue.', 'Interactive enough to learn from.'] as const;
const ARC = ['Story', 'Choice', 'Insight', 'Action'] as const;

/** Screens laid out 3 + 2 so each one can be shown larger. */
const WALKTHROUGH_ROWS = [SNAK_WALKTHROUGH.slice(0, 3), SNAK_WALKTHROUGH.slice(3)];
export function SlideAttentionProblem({}: SlideProps) {
  return <InteriorSlide surface banner={<SlideBanner data-id="element-234" data-mp="y5xajw8bs6in">Every interaction creates a signal.</SlideBanner>} data-id="element-233" data-mp="2vx9t8egycyx3">
      <div className="flex flex-shrink-0 flex-col gap-[clamp(1rem,2vw,2rem)] lg:flex-row lg:items-start lg:justify-between lg:gap-12" data-id="element-235" data-mp="u3typ6hquzwu">
        <div className="min-w-0 lg:max-w-[42%]" data-id="element-236" data-mp="2nlhug0mntunh">
          <motion.p initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.45
        }} className="mb-3 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-237" data-mp="2nkpy2nl8nsw5">
            The attention problem
          </motion.p>

          <motion.h2 initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.12,
          duration: 0.55
        }} className="text-[clamp(1.8rem,3.4vw,3.2rem)] font-extrabold uppercase leading-[0.98] tracking-[-0.045em] text-[var(--fg)]" data-id="element-238" data-mp="36csi88x39rb5">
            HCPs don’t lack content.
            <span className="mt-1 block text-[var(--brand)]" data-id="element-239" data-mp="32djov55ynvg4">
              They lack time.
            </span>
          </motion.h2>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.28,
        duration: 0.5
      }} className="max-w-2xl flex-shrink-0 border-l-2 border-[var(--brand)] pl-5" data-id="element-240" data-mp="1qwtgpghz8tjv">
          <p className="text-[clamp(0.65rem,0.9vw,0.8rem)] font-bold uppercase tracking-[0.16em] text-[var(--muted)]" data-id="element-241" data-mp="1efqzkpsirssf">
            So we created the Snak.
          </p>
          <p className="mt-1.5 text-[clamp(1.15rem,1.8vw,1.75rem)] font-extrabold leading-tight tracking-[-0.025em] text-[var(--fg)]" data-id="element-242" data-mp="1nfntb5trnhuc">
            A short story you participate in.
          </p>
          <p className="mt-[clamp(0.5rem,0.9vw,0.8rem)] text-[clamp(0.8rem,1.05vw,1rem)] leading-relaxed text-[var(--muted)]" data-id="element-243" data-mp="uqqb12pk1l1l">
            A Snak turns complex content into a short, engaging journey. HCPs
            don’t just read or watch — they choose, react and discover. Every
            interaction creates insight.
          </p>
          <ul className="mt-[clamp(0.6rem,1.1vw,1rem)] flex flex-wrap gap-x-[clamp(1rem,2vw,2rem)] gap-y-2" data-id="element-244" data-mp="gw7f9a449fs9">
            {PRINCIPLES.map((principle, index) => <li key={principle} className="flex items-center gap-2" data-id="element-245" data-mp="3behseu97bh4i" data-mpi={index}>
                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[var(--brand)] text-[10px] font-bold text-white" data-id="element-246" data-mp="3gbrsd3u7vb6l">
                  {index + 1}
                </span>
                <span className="text-[clamp(0.78rem,1vw,0.95rem)] font-semibold text-[var(--fg)]" data-id="element-247" data-mp="3p4uzkue9qrss">
                  {principle}
                </span>
              </li>)}
          </ul>
        </motion.div>
      </div>

      <motion.figure initial={{
      opacity: 0,
      y: 26
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.4,
      duration: 0.6
    }} className="mt-[clamp(1rem,2vw,1.75rem)] flex min-h-0 flex-1 flex-col" data-id="element-248" data-mp="2vtt0sbi8sw5">
        <figcaption className="mb-[clamp(0.4rem,0.8vw,0.7rem)] flex flex-shrink-0 flex-wrap items-center justify-between gap-x-6 gap-y-1" data-id="element-249" data-mp="29nul1rykyxh5">
          <span className="text-[clamp(0.58rem,0.8vw,0.72rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-250" data-mp="2dqhetyetasgz">
            A live Boehringer Snak — one short story, screen by screen
          </span>
          <span className="flex items-center gap-[clamp(0.4rem,0.9vw,0.9rem)]" data-id="element-251" data-mp="21ef31nyb7xj0">
            {ARC.map((step, index) => <React.Fragment key={step} data-id="element-252" data-mp="1wgdn3bw6kebr" data-mpi={index}>
                {index > 0 && <ArrowRightIcon className="h-[clamp(0.75rem,1vw,1rem)] w-[clamp(0.75rem,1vw,1rem)] flex-shrink-0 text-[var(--brand)]" aria-hidden="true" data-id="element-253" data-mp="1sftkhssu8ii8" />}
                <span className="text-[clamp(0.62rem,0.88vw,0.85rem)] font-extrabold uppercase tracking-[0.18em] text-[var(--fg)]" data-id="element-254" data-mp="2k3vg1ar6ligb">
                  {step}
                </span>
              </React.Fragment>)}
          </span>
        </figcaption>

        <div className="flex min-h-0 flex-1 flex-col gap-[clamp(0.5rem,1vw,0.9rem)] rounded-[1.5rem] bg-white p-[clamp(0.6rem,1.1vw,1rem)] shadow-sm" data-id="element-255" data-mp="14ppkrwirt2wi">
          {WALKTHROUGH_ROWS.map((row, rowIndex) => <div key={rowIndex} className="flex min-h-0 flex-1 items-center justify-center gap-[clamp(0.5rem,1vw,0.9rem)]" data-id="element-256" data-mp="117jpolcnys6k" data-mpi={rowIndex}>
              {row.map(screen => <figure key={screen.alt} className="flex h-full min-h-0 min-w-0 max-w-[33%] flex-col items-center justify-center" data-id="element-257" data-mp="s5sitd6e0fb4">
                  <div className="flex min-h-0 w-full flex-1 items-center justify-center" data-id="element-258" data-mp="ifqx2s1ib8zd">
                    <PdfImage crop={screen.crop} alt={screen.alt} className="h-full w-auto max-w-full rounded-xl object-contain shadow-[0_8px_24px_rgba(23,32,51,0.12)] ring-1 ring-[var(--fg)]/10" data-id="element-259" data-mp="n8ak0fm33ggu" />
                  </div>
                  <figcaption className="mt-[clamp(0.25rem,0.5vw,0.45rem)] line-clamp-2 max-w-[22rem] flex-shrink-0 text-center text-[clamp(0.6rem,0.82vw,0.8rem)] font-semibold leading-snug text-[var(--muted)]" data-id="element-260" data-mp="3vodiburb3ssi">
                    {screen.caption}
                  </figcaption>
                </figure>)}
            </div>)}
        </div>
      </motion.figure>
    </InteriorSlide>;
}